import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { getLatestEventTime } from './commsTraceStatus';
import type { CommsTraceJourney, CommsTraceUserResult } from './types';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const UNKNOWN_EVENT_DATE_KEY = '__unknown__';

export type UserResultGroup = {
  userId: string;
  result: CommsTraceUserResult;
};

export type EventDateGroup = {
  dateKey: string;
  label: string;
  count: number;
  journeys: CommsTraceJourney[];
};

export const getJourneyEventTimestamp = (journey: CommsTraceJourney): string | null =>
  getLatestEventTime(journey);

export const getJourneyEventDate = (
  journey: CommsTraceJourney,
  timezone: string,
): string | null => {
  const timestamp = getJourneyEventTimestamp(journey);
  if (!timestamp) return null;
  try {
    const date = dayjs(timestamp).tz(timezone);
    if (!date.isValid()) return null;
    return date.format('YYYY-MM-DD');
  } catch (e) {
    return null;
  }
};

export const formatEventDateLabel = (dateKey: string, timezone: string): string => {
  if (dateKey === UNKNOWN_EVENT_DATE_KEY) return 'Unknown date';
  try {
    const date = dayjs.tz(dateKey, timezone);
    if (!date.isValid()) return dateKey;
    return date.format('ddd MMM D');
  } catch (e) {
    return dateKey;
  }
};

export const formatEventTime = (
  timestamp: string | null | undefined,
  timezone: string,
): string => {
  if (!timestamp) return '-';
  try {
    const date = dayjs(timestamp).tz(timezone);
    if (!date.isValid()) return '-';
    return date.format('HH:mm:ss');
  } catch (e) {
    return '-';
  }
};

/** Preserve requested user order when provided; otherwise use response order. */
export const orderUserResults = (
  users: Record<string, CommsTraceUserResult>,
  preferredUserIDs?: string[] | null,
): UserResultGroup[] => {
  const preferred = preferredUserIDs || [];
  const ordered: UserResultGroup[] = [];
  const seen = new Set<string>();

  preferred.forEach(userId => {
    const result = users[userId];
    if (!result) return;
    ordered.push({ userId, result });
    seen.add(userId);
  });

  Object.entries(users).forEach(([userId, result]) => {
    if (seen.has(userId)) return;
    ordered.push({ userId, result });
  });

  return ordered;
};

const compareTimestampsDesc = (a: string | null, b: string | null): number => {
  if (a && b) return b.localeCompare(a);
  if (a) return -1;
  if (b) return 1;
  return 0;
};

export const groupJourneysByEventDate = (
  journeys: CommsTraceJourney[],
  timezone: string,
): EventDateGroup[] => {
  const byDate = new Map<string, CommsTraceJourney[]>();

  journeys.forEach(journey => {
    const dateKey = getJourneyEventDate(journey, timezone) || UNKNOWN_EVENT_DATE_KEY;
    if (!byDate.has(dateKey)) byDate.set(dateKey, []);
    byDate.get(dateKey)!.push(journey);
  });

  const knownKeys = Array.from(byDate.keys())
    .filter(key => key !== UNKNOWN_EVENT_DATE_KEY)
    .sort((a, b) => b.localeCompare(a));

  if (byDate.has(UNKNOWN_EVENT_DATE_KEY)) {
    knownKeys.push(UNKNOWN_EVENT_DATE_KEY);
  }

  return knownKeys.map(dateKey => {
    const groupJourneys = [...(byDate.get(dateKey) || [])].sort((left, right) =>
      compareTimestampsDesc(
        getJourneyEventTimestamp(left),
        getJourneyEventTimestamp(right),
      ),
    );
    return {
      dateKey,
      label: formatEventDateLabel(dateKey, timezone),
      count: groupJourneys.length,
      journeys: groupJourneys,
    };
  });
};
